// Which map the board draws (issue 834): the scene on this screen. For a
// player (and the DM looking through a player's eyes) that is the live scene,
// the table's `map`; for the DM it is whichever scene they are looking at,
// read from the scene list only the DM's view carries.
//
// Floors (qa-runs#1160 A2): each floor may have its own map picture. The scene's map is the ground floor's; a floor with
// a map of its own shows it, any other floor with none shows the empty grid. A player is sent the scene's map only while a
// token of theirs is on the ground floor, and each floor's map only on the floors they hold.
import { useEffect, useState } from 'react';
import { groundFloor, hasFloors, sceneFloors, type MapInfo } from '@dragons-table/vtt-shared';
import { useShell } from '../shell/context';
import { useTableState } from '../table/TableState';
import { floorView, viewedFloor } from './floorView';

/**
 * Which map a scene the DM is looking at has (qa-runs#1465): its own from the scene list, or none. A `null` there means
 * "this scene has no map", so it never falls through to the live scene's (`table.map`): that made an empty scene show
 * the live one's picture, which went when another scene went live.
 */
export function sceneMapOf(scenes: ReadonlyArray<{ id: string; map: MapInfo | null }>, sceneId: string): MapInfo | null {
  return scenes.find((s) => s.id === sceneId)?.map ?? null;
}

export function useBoardMap(): MapInfo | null {
  const { table } = useShell();
  const { scene, store, meId, role, viewingAs } = useTableState();
  const [picked, setPicked] = useState(floorView.get());
  useEffect(() => floorView.subscribe(setPicked), []);
  // The scene's own map, or none (qa-runs#1465): a scene with no map is the empty grid, never the live scene's. Only a
  // scene not in the list yet (one just added, before the list is sent again) is drawn as nothing is known of it: empty.
  const sceneMap = !scene || !table.scenes ? table.map : sceneMapOf(table.scenes, scene.id);
  const st = store.state;
  if (!hasFloors(st.scene)) return sceneMap;
  const floors = sceneFloors(st.scene);
  const at = viewedFloor({ scene: st.scene, objects: st.objects as Record<string, { floor?: string; owners?: string[] }> }, meId, role === 'dm' && !viewingAs, picked);
  const f = floors.find((x) => x.id === at);
  if (f?.map) return { ...f.map };
  return at === groundFloor(floors) ? sceneMap : null;
}

/**
 * The scene a map change is for (qa-runs#1465): always named, never left to the server's "the live one". The DM's is the
 * scene on their screen; a co-DM, who is always on the live scene, names it from the list. Looking through a player's
 * eyes there is no scene of one's own on screen, so none: the change is not offered.
 */
export function mapTarget(scene: { id: string } | null, viewingAs: boolean, scenes: ReadonlyArray<{ id: string; live: boolean }> | undefined): string | null {
  if (viewingAs) return null;
  if (scene) return scene.id;
  return scenes?.find((s) => s.live)?.id ?? null;
}

export function useMapTarget(): string | null {
  const { table } = useShell();
  const { scene, viewingAs } = useTableState();
  return mapTarget(scene, viewingAs !== null, table.scenes);
}

/** The DM's staging state: the scene on their screen, and the one the players are on, when they differ. */
export function useStaging(): { staged: { id: string; name: string }; live: { id: string; name: string } } | null {
  const { table } = useShell();
  const { scene } = useTableState();
  if (!scene || !table.scenes) return null;
  const live = table.scenes.find((s) => s.live);
  const here = table.scenes.find((s) => s.id === scene.id);
  if (!live || !here || live.id === here.id) return null;
  return { staged: { id: here.id, name: here.name }, live: { id: live.id, name: live.name } };
}

/**
 * The version the map is asked at (issue 851): a player's map on a fogged scene is served masked, so it is
 * fetched as `?v=<fog.rev>` and fetched again whenever that moves. The DM's is always the original — looking
 * through a player's eyes too, where the covered squares are painted on the board instead — so none.
 */
export function mapVersion(role: string, viewingAs: boolean, fog: { rev: number } | null | undefined): number | undefined {
  if (role === 'dm' || viewingAs) return undefined;
  return fog?.rev ?? 0;
}

export function useMapVersion(): number | undefined {
  const { role, viewingAs, store } = useTableState();
  return mapVersion(role, viewingAs !== null, store.state.fog);
}

/**
 * A player's map tiles (issue 1144, slice 3; design/30 §5.3): with the scene's vision or the manual fog on, their fog
 * names each tile they may see something of and the hash of that view, and the board draws the map from those tiles.
 * Null draws the map whole: the DM's (looking through a player's eyes too, where the board paints what is hidden), and a
 * player's with both off. On another floor than the ground with its own map (qa-runs#1160), that floor's tiles
 * (`tilesOn`), masked by its own explored squares; a floor they were sent none of shows nothing.
 */
export function mapTiles(role: string, viewingAs: boolean, fog: { tiles?: Record<string, string>; tilesOn?: Record<string, Record<string, string>> } | null | undefined, floor: string | null = null): Record<string, string> | null {
  if (role === 'dm' || viewingAs) return null;
  if (floor !== null && fog?.tilesOn) return fog.tilesOn[floor] ?? {};
  return fog?.tiles ?? null;
}

export function useMapTiles(): Record<string, string> | null {
  const { role, viewingAs, store, meId } = useTableState();
  const [picked, setPicked] = useState(floorView.get());
  useEffect(() => floorView.subscribe(setPicked), []);
  const st = store.state;
  // The floor in view, when it is not the ground floor: its own map's tiles. Read as the board's map is (`useBoardMap`).
  let floor: string | null = null;
  if (hasFloors(st.scene)) {
    const floors = sceneFloors(st.scene);
    const at = viewedFloor({ scene: st.scene, objects: st.objects as Record<string, { floor?: string; owners?: string[] }> }, meId, role === 'dm' && !viewingAs, picked);
    if (at !== groundFloor(floors) && floors.find((x) => x.id === at)?.map) floor = at;
  }
  return mapTiles(role, viewingAs !== null, st.fog as { tiles?: Record<string, string>; tilesOn?: Record<string, Record<string, string>> } | null, floor);
}
